import { React, useState, useMemo, useCallback, useEffect, useRef } from "react";
import moment from "moment";
import { snakeCase } from "lodash";
import clsx from "clsx";
import { t } from "i18next";
import { useDispatch, useSelector } from "react-redux";

import { Icon } from "components";
import {
  setVisibleCalendar,
  setSelectedDate,
  getVisibleInPersonTour,
  getVisibleLiveVideoTour,
  getVisibleSelfGuidedTour,
  getSelfTourLink,
  setDisablePreviousDates,
  getSelectedDate,
  selectAPIKey,
  selectPropertyId,
  getScheduleDateAvail,
  setSelectedTourType,
  setSlotsLoading,
  selectSlotsLoading,
  selectSelectedOptions,
  selectAvailableSlots,
  selectAllData,
  setAvailableTimeSlots,
  setSelectedTourDate,
  setSelectedTourTime,
  getShouldFetchSlots,
  getHeaderBgColor,
} from "slices";
import {
  getAvailableDates,
  handleTourResponse,
  filterByDate,
  getAmPmSlotsSeparated,
  getFormattedTimeSlots,
  createEventSignal,
  moveTourTypeToFront,
} from "utils";
import useClickOutside from "hooks/useClickOutside";
import { getAppSSID } from "slices/window-slice/index";

import { TourSchedulerSkeleton } from "./TourSchedularSkeleton";
import ShowTimeSlots from "./ShowTimeSlots";

const TourSchedulerForm = ({ inputValues, setInputValues }) => {
  const dispatch = useDispatch();
  const timeSlotsRef = useRef(null);
  const isInPersonTourEnabled = useSelector(getVisibleInPersonTour);
  const isVideoTourEnabled = useSelector(getVisibleLiveVideoTour);
  const isSelfGuidedTourEnabled = useSelector(getVisibleSelfGuidedTour);
  const self_tour_link = useSelector(getSelfTourLink);
  const apiKey = useSelector(selectAPIKey);
  const scheduleDateAvail = useSelector(getScheduleDateAvail);
  const propertyId = useSelector(selectPropertyId);
  const selectedDate = useSelector(getSelectedDate);
  const [shouldShowAllTimeSlots, setShouldShowAllTimeSlots] = useState(false);
  const isShouldFetchSlots = useSelector(getShouldFetchSlots);
  const SSID = useSelector(getAppSSID);
  const allData = useSelector(selectAllData);
  const slotsLoading = useSelector(selectSlotsLoading);
  const { tourType, date, time } = useSelector(selectSelectedOptions);
  const { date: availableDateSlots, time: availableTimeSlots } = useSelector(selectAvailableSlots);
  const themeColor = useSelector(getHeaderBgColor);

  const hexToRgba = (hex, opacity) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

    if (result) {
      const r = parseInt(result[1], 16);
      const g = parseInt(result[2], 16);
      const b = parseInt(result[3], 16);

      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    return hex;
  };

  const selectedBgColor = hexToRgba(themeColor, 0.08);

  const fetchSlots = async (tourType, date, time) => {
    try {
      dispatch(setSlotsLoading(true));
      dispatch(setSelectedTourType(tourType));

      let URL = `?property_id=${propertyId}&api_key=${encodeURIComponent(apiKey)}&tour_type=${tourType}`;

      if (date) {
        URL = URL + `&date=${date}`;
      }

      const { data } = await getAvailableDates(URL);

      if (!data?.length) {
        dispatch(setSlotsLoading(false));

        return;
      }

      handleTourResponse(data, true, tourType, time);
    } catch (error) {
      dispatch(setSlotsLoading(false));
    }
  };

  const handleTourClick = (label) => {
    const clickedTourType = snakeCase(label);

    if (label === "selfGuidedTour" && self_tour_link) {
      dispatch(setSelectedTourType(clickedTourType));
      window.open(self_tour_link, "_blank");

      return;
    }

    const clickedTourTypeData = allData.find((item) => item[clickedTourType])?.[clickedTourType] || null;

    // If the clicked tour type is the same as the current tour type, do nothing
    if (tourType === clickedTourType) {
      return;
    }

    // If allData contains clicked tour type data, move it to the front and handle the response
    if (clickedTourTypeData) {
      const updatedData = moveTourTypeToFront(allData, clickedTourType);
      handleTourResponse(updatedData, true, clickedTourType, time);
    } else {
      // Otherwise, fetch slots for the clicked tour type
      fetchSlots(clickedTourType);
    }
  };

  const handleDateClick = (value) => {
    const slotData = allData?.[0]?.[tourType];

    // Safety check: Don't proceed if slot data is not available
    if (!slotData || !Array.isArray(slotData) || slotData.length === 0) {
      return;
    }

    const defaultTimeSlots = filterByDate(slotData, value);
    const selectedTimeSlot = defaultTimeSlots?.[0]?.time_slot;
    const properTimeSlots = getAmPmSlotsSeparated(getFormattedTimeSlots(defaultTimeSlots));

    dispatch(setSelectedTourDate(value));
    dispatch(setSelectedTourTime(selectedTimeSlot));
    dispatch(setAvailableTimeSlots(properTimeSlots));
    setInputValues((prev) => ({ ...prev, date: value, time: selectedTimeSlot }));
  };

  const handleTimeClick = (value) => {
    dispatch(setSelectedTourTime(value));
    setInputValues((prev) => ({ ...prev, time: value }));
  };

  // When date changes from calendar
  useEffect(() => {
    if (selectedDate) {
      handleDateClick(moment(selectedDate).format("YYYY-MM-DD"));
    }
  }, [selectedDate]);

  const requestOption = {
    tag: "SAT_MORE_CLICKED",
    metric: "Click",
    propertyId: propertyId,
    sessionId: SSID,
    appSubsection: "schedule a tour date slots",
  };

  const handleShowAll = useCallback(() => {
    const myDate = moment(date).format("MMM DD, YYYY");
    dispatch(setSelectedDate(myDate));
    dispatch(setVisibleCalendar(true));
    dispatch(setDisablePreviousDates(true));
    let eventKeys = JSON.parse(localStorage.getItem(`events_${SSID}`)) || [];
    eventKeys.push("SAT more click");
    localStorage.setItem(`events_${SSID}`, JSON.stringify(eventKeys));

    createEventSignal(requestOption);
  }, [dispatch, date]);

  // Select default tour type from available options or get slected payload from
  // ML API to fetch slots data
  useEffect(() => {
    if (!isShouldFetchSlots) {
      if (!tourType) {
        handleTourClick("inPersonTour");
      }
    } else {
      const tourOptions = {
        in_person_tour: isInPersonTourEnabled,
        live_video_tour: isVideoTourEnabled,
        self_guided_tour: isSelfGuidedTourEnabled,
      };

      const timer = setTimeout(() => {
        // Fetch slots for the first enabled tour type i.e. in_person_tour
        for (const [appointmentType, isEnabled] of Object.entries(tourOptions)) {
          if (isEnabled) {
            fetchSlots(appointmentType);
            break;
          }
        }
      }, 1);

      return () => clearTimeout(timer);
    }
  }, [isShouldFetchSlots, isInPersonTourEnabled, isVideoTourEnabled, isSelfGuidedTourEnabled]);

  useClickOutside(timeSlotsRef, () => {
    setShouldShowAllTimeSlots(false);
  });

  // Pick date slots to render in main date slots view
  const dateLabels = useMemo(() => {
    let labels = [];

    if (date) {
      const selectedDateIndex = availableDateSlots.findIndex((d) => moment(d).isSame(date, "day"));
      const previousDate = selectedDateIndex > 0 ? availableDateSlots[selectedDateIndex - 1] : null;
      const currentDate = availableDateSlots[selectedDateIndex];
      const nextDate = selectedDateIndex < availableDateSlots.length - 1 ? availableDateSlots[selectedDateIndex + 1] : null;

      if (previousDate) {
        labels.push(previousDate);
        labels.push(currentDate);

        if (nextDate) {
          labels.push(nextDate);
        }
      } else {
        labels.push(...availableDateSlots.slice(0, 3));
      }
    } else {
      labels.push(...availableDateSlots.slice(0, 3));
    }

    return labels;
  }, [date, availableDateSlots]);

  // Pick time slots to render in main time slots view
  const timeSlots = useMemo(() => {
    const allSlots = [...availableTimeSlots.morning, ...availableTimeSlots.afternoon];
    const selectedIndex = allSlots.findIndex((slot) => slot.value === time);

    // Case 1: If the selected time is within first 7 slots
    if (selectedIndex === -1 || selectedIndex < 7) {
      return allSlots.slice(0, 7);
    }

    // Case 2: If the selected time is within the last 7 slots
    if (selectedIndex >= allSlots.length - 7) {
      return allSlots.slice(allSlots.length - 7);
    }

    // Case 3: If the selected time is in the middle, return 7 slots centered around the selected slot
    const start = Math.max(selectedIndex - 3, 0);
    const end = Math.min(selectedIndex + 4, allSlots.length);

    return allSlots.slice(start, end);
  }, [time, availableTimeSlots.morning, availableTimeSlots.afternoon]);

  const hideDateAndTime = useMemo(() => {
    return self_tour_link && tourType === "self_guided_tour";
  }, [tourType]);

  const availableTourTypes = {
    inPersonTour: isInPersonTourEnabled,
    liveVideoTour: isVideoTourEnabled,
    selfGuidedTour: isSelfGuidedTourEnabled,
  };

  return (
    <div data-testid="scheduleTourForm">
      <div className="hayley-mb-3 hayley-flex hayley-items-start">
        <p className="hayley-m-0  hayley-w-20 hayley-flex-shrink-0 hayley-text-sm hayley-font-semibold hayley-text-custom-8">{t("tourType")}</p>

        <div className="hayley-flex hayley-flex-wrap hayley-gap-2 hayley-text-xs">
          {Object.entries(availableTourTypes).map(([label, isAvailable]) =>
            isAvailable ? (
              <div
                key={label}
                className={clsx(
                  "hayley-flex hayley-cursor-pointer hayley-items-center hayley-whitespace-nowrap hayley-rounded-[20px] hayley-border hayley-border-solid hayley-px-3 hayley-py-[6px] hayley-text-[#333333] hayley-transition-all hayley-duration-200",
                  {
                    "hayley-border-[#999999] hayley-bg-white": tourType !== snakeCase(label),
                    "hayley-font-semibold": tourType === snakeCase(label),
                  }
                )}
                style={{
                  ...(tourType === snakeCase(label) ? { borderColor: themeColor, backgroundColor: selectedBgColor } : {}),
                }}
                onMouseEnter={(e) => {
                  if (tourType !== snakeCase(label)) {
                    e.currentTarget.style.borderColor = themeColor;
                  }
                }}
                onMouseLeave={(e) => {
                  if (tourType !== snakeCase(label)) {
                    e.currentTarget.style.borderColor = "#999999";
                  }
                }}
                onClick={() => handleTourClick(label)}
                data-testid={label}
              >
                {t(label)}
                {label == "selfGuidedTour" && self_tour_link && (
                  <Icon
                    iconType={"OpenLinkIcon"}
                    className={clsx("hayley-w-4", {
                      "hayley-text-[#333333]": tourType !== snakeCase(label),
                      "hayley-font-semibold hayley-text-[#333333]": tourType === snakeCase(label),
                    })}
                  />
                )}
              </div>
            ) : null
          )}
        </div>
      </div>

      {scheduleDateAvail || hideDateAndTime || (!availableDateSlots.length && !slotsLoading) ? null : (
        <>
          <div data-testid="scheduleTourForm_daySlots" className="hayley-mb-3 hayley-flex hayley-items-start">
            <p className="hayley-m-0 hayley-w-20 hayley-flex-shrink-0 hayley-text-sm hayley-font-semibold hayley-text-custom-8">{t("date")}</p>
            {slotsLoading ? (
              <TourSchedulerSkeleton />
            ) : (
              <div className="hayley-flex hayley-flex-wrap hayley-items-center hayley-gap-2 hayley-text-xs">
                {dateLabels.map((dateLabel, index) => {
                  let label = moment(dateLabel).format("ddd, MMM D");

                  if (moment(dateLabel).isSame(moment(), "day")) {
                    label = "today";
                  } else if (moment(dateLabel).isSame(moment().add(1, "day"), "day")) {
                    label = "tomorrow";
                  }

                  return (
                    <div
                      data-testid="date-slots-bubbles"
                      key={index}
                      className={clsx(
                        "hayley-cursor-pointer hayley-whitespace-nowrap hayley-rounded-[20px] hayley-border hayley-border-solid hayley-px-3 hayley-py-[6px] hayley-text-[#333333] hayley-transition-all hayley-duration-200",
                        {
                          "hayley-border-[#999999] hayley-bg-white": date !== dateLabel,
                          "hayley-font-semibold": date === dateLabel,
                        }
                      )}
                      style={{
                        ...(date === dateLabel ? { borderColor: themeColor, backgroundColor: selectedBgColor } : {}),
                      }}
                      onMouseEnter={(e) => {
                        if (date !== dateLabel) {
                          e.currentTarget.style.borderColor = themeColor;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (date !== dateLabel) {
                          e.currentTarget.style.borderColor = "#999999";
                        }
                      }}
                      onClick={() => handleDateClick(dateLabel)}
                    >
                      {t(label)}
                    </div>
                  );
                })}
                <span
                  className="hayley-shrink-0 hayley-cursor-pointer hayley-rounded-[20px] hayley-border hayley-border-solid hayley-border-[#999999] hayley-bg-white hayley-px-3 hayley-py-[6px] hayley-text-[#333333] hayley-transition-all hayley-duration-200"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = themeColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#999999";
                  }}
                  onClick={handleShowAll}
                >
                  {t("more")}...
                </span>
              </div>
            )}
          </div>

          <div data-testid="scheduleTourForm_timeSlots" className="hayley-mb-4 hayley-flex ">
            <p className="hayley-m-0 hayley-w-20 hayley-flex-shrink-0 hayley-pt-[1px] hayley-text-sm hayley-font-semibold hayley-text-custom-8">{t("time")}</p>
            {slotsLoading ? (
              <TourSchedulerSkeleton />
            ) : (
              <>
                <div className="hayley-flex hayley-flex-wrap hayley-items-center hayley-gap-2 hayley-text-xs">
                  {timeSlots.length ? (
                    <>
                      {timeSlots.map(({ label, value }) => (
                        <>
                          <div
                            data-testid="time-slots-bubbles"
                            key={value}
                            className={clsx(
                              "hayley-cursor-pointer hayley-whitespace-nowrap hayley-rounded-[20px] hayley-border hayley-border-solid hayley-px-3 hayley-py-[6px] hayley-text-[#333333] hayley-transition-all hayley-duration-200",
                              {
                                "hayley-border-[#999999] hayley-bg-white": time !== value,
                                "hayley-font-semibold": time === value,
                              }
                            )}
                            style={{
                              ...(time === value ? { borderColor: themeColor, backgroundColor: selectedBgColor } : {}),
                            }}
                            onMouseEnter={(e) => {
                              if (time !== value) {
                                e.currentTarget.style.borderColor = themeColor;
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (time !== value) {
                                e.currentTarget.style.borderColor = "#999999";
                              }
                            }}
                            onClick={() => handleTimeClick(value)}
                          >
                            {label}
                          </div>
                        </>
                      ))}

                      <span
                        className="hayley-cursor-pointer hayley-rounded-[20px] hayley-border hayley-border-solid hayley-border-[#999999] hayley-bg-white hayley-px-3 hayley-py-[6px] hayley-text-[#333333] hayley-transition-all hayley-duration-200"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = themeColor;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = "#999999";
                        }}
                        onClick={() => {
                          createEventSignal({ ...requestOption, appSubsection: "schedule a tour time slots" });
                          setShouldShowAllTimeSlots(true);
                        }}
                      >
                        {t("more")}...
                      </span>
                    </>
                  ) : null}
                </div>
              </>
            )}
          </div>
        </>
      )}
      {shouldShowAllTimeSlots && (
        <ShowTimeSlots
          time={time}
          handleClose={() => setShouldShowAllTimeSlots(false)}
          inputValues={inputValues}
          timeSlotsRef={timeSlotsRef}
          availableTimeSlots={availableTimeSlots}
          handleClick={handleTimeClick}
        />
      )}
    </div>
  );
};

export default TourSchedulerForm;
